import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, UploadCloud, Lightbulb, Lock } from "lucide-react";
import TutorialHelp from "@/components/TutorialHelp";

const DOMAINS = [
  "Computer Science", "AI/ML", "Data Science", "Public Health", "Medical Sciences",
  "Civil Engineering", "Climate Science", "Environment", "Economics",
  "Education", "Social Sciences", "Multidisciplinary",
];

export default function IdeaNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", dataset_link: "",
    // New required fields (B.1-B.3) — stage drives separate routing on the
    // backend; thesis_to_paper and raw_idea use different review flows.
    stage: "", whatsapp: "", preferred_contact: "email",
    // Advanced
    methodology: "", reference_papers: "", target_quartile: "Q1", target_venue: "",
    domain: "Multidisciplinary", keywords: "", notes: "",
    taxonomy_node_id: "",
  });
  const [advanced, setAdvanced] = useState(false);
  const [fileName, setFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [topNodes, setTopNodes] = useState([]);

  useEffect(() => {
    api.get("/research-ideas", { params: { limit: 6 } }).then((r) => setSuggestions(r.data || [])).catch(() => {});
    api.get("/taxonomy", { params: { domain: "research_ideas" } })
      .then((r) => setTopNodes((r.data || []).filter((n) => !n.parent_id)))
      .catch(() => setTopNodes([]));
  }, []);

  const applySuggestion = (idea) => {
    setForm((f) => ({
      ...f,
      title: idea.title || f.title,
      description: (idea.overview || "") + (idea.research_gap ? `\n\nResearch gap:\n${idea.research_gap}` : ""),
      domain: idea.tags?.[0] || f.domain,
      keywords: (idea.tags || []).join(", ") || f.keywords,
    }));
    toast.success("Suggestion loaded — feel free to edit");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stage) { toast.error("Please pick an idea stage"); return; }
    if (form.description.length < 50) { toast.error("Description needs at least 50 characters"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      };
      const res = await api.post("/ideas", payload);
      toast.success("Idea submitted — awaiting admin review.");
      navigate(`/dashboard/ideas/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit idea");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="idea-new-page" className="max-w-3xl mx-auto">
      <Link to="/dashboard/ideas" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2" data-testid="idea-new-back">
        <ArrowLeft className="w-3.5 h-3.5" /> BACK TO IDEAS
      </Link>
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-6">SUBMIT NEW IDEA</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        Share your <em className="italic text-kb-primary">research idea</em>.
      </h1>
      <p className="text-sm text-kb-ink-muted mt-2">
        Tell us the essentials. Our admin team will review for feasibility and route to a matched mentor — no need to pick one yourself.
      </p>

      <div className="mt-6">
        <TutorialHelp tutorialKey="idea-submission" />
      </div>

      {/* Suggested curated ideas — quick way to start */}
      {suggestions.length > 0 && (
        <div className="mt-8 kb-card p-5 bg-gradient-to-br from-amber-50/40 to-white" data-testid="idea-new-suggestions">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-amber-700">NOT SURE WHERE TO START?</div>
              <h3 className="font-serif text-lg text-kb-ink mt-1">Pick from <em className="italic text-kb-primary">curated suggestions</em></h3>
            </div>
            <Link to="/research-ideas" className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline inline-flex items-center gap-1">
              See all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {suggestions.slice(0, 4).map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => applySuggestion(s)}
                className="text-left p-3 border border-slate-200 rounded-sm bg-white hover:border-kb-primary hover:shadow-sm transition-all group"
                data-testid={`idea-suggestion-${s.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <Lightbulb className="w-3.5 h-3.5 text-kb-primary mt-0.5 shrink-0" />
                  {s.is_premium && <span className="kb-mono text-[8px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0.5 rounded-sm inline-flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" />PREMIUM</span>}
                </div>
                <div className="font-medium text-sm text-kb-ink mt-1.5 line-clamp-2 group-hover:text-kb-primary transition-colors">{s.title}</div>
                <div className="text-[11px] text-kb-ink-muted line-clamp-2 mt-1">{s.overview}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 kb-card p-8 space-y-5" data-testid="idea-new-form">
        <div>
          <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Idea stage *</label>
          <select
            required
            className="kb-input mt-1.5"
            value={form.stage}
            onChange={(e) => setForm({ ...form, stage: e.target.value })}
            data-testid="idea-stage-select"
          >
            <option value="">Select stage…</option>
            <option value="raw_idea">Initial raw idea (just a spark — need help shaping it)</option>
            <option value="worked_idea">Worked idea (have a direction, need guidance)</option>
            <option value="thesis_to_paper">Thesis → Paper (convert existing thesis to publishable paper)</option>
            <option value="data_collected">Data collected (need help with analysis / writing)</option>
            <option value="draft_in_progress">Draft in progress (need review / editing)</option>
            <option value="revision_requested">Revision requested (handling reviewer feedback)</option>
            <option value="publication_ready">Publication-ready (need target journal / submission help)</option>
          </select>
          <div className="kb-mono text-[9px] text-kb-ink-light mt-1">
            Thesis-to-paper and fresh ideas follow different review flows — picking the right stage speeds up matching.
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">WhatsApp number</label>
            <input
              name="whatsapp"
              type="tel"
              className="kb-input mt-1.5"
              value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              placeholder="+977 98xxxxxxxx"
              data-testid="idea-whatsapp-input"
            />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Preferred contact for follow-up</label>
            <select
              className="kb-input mt-1.5"
              value={form.preferred_contact}
              onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })}
              data-testid="idea-contact-select"
            >
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="phone">Phone call</option>
              <option value="in_platform">In-platform chat</option>
            </select>
          </div>
        </div>

        <div>
          <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Title *</label>
          <input required name="title" className="kb-input mt-1.5" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="A short, descriptive working title" data-testid="idea-title-input" />
        </div>

        <div>
          <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Description / Abstract * (min 50 chars)</label>
          <textarea required name="description" rows={6} className="kb-input mt-1.5 resize-none" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What problem are you solving? Why does it matter? What is your approach?"
            data-testid="idea-description-input" />
          <div className="kb-mono text-[10px] text-kb-ink-light mt-1">{form.description.length} characters</div>
        </div>

        <div>
          <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Dataset link (optional)</label>
          <input className="kb-input mt-1.5" value={form.dataset_link}
            onChange={(e) => setForm({ ...form, dataset_link: e.target.value })}
            placeholder="https://… GitHub, Drive, OSF, Zenodo" data-testid="idea-dataset-input" />
        </div>

        <div>
          <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Or attach supporting document (optional)</label>
          <label className="mt-1.5 block border-2 border-dashed border-slate-300 rounded-sm p-5 text-center bg-slate-50 hover:bg-slate-100 hover:border-kb-primary cursor-pointer transition" data-testid="idea-file-zone">
            <UploadCloud className="w-5 h-5 text-kb-ink-muted mx-auto" />
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-2">
              {fileName || "CLICK TO ATTACH (PDF, DOCX, CSV)"}
            </div>
            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.csv,.txt"
              onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
          </label>
        </div>

        <button type="button" onClick={() => setAdvanced(!advanced)}
          className="w-full text-left border border-slate-200 rounded-sm px-4 py-3 flex items-center justify-between hover:border-kb-primary transition"
          data-testid="idea-advanced-toggle">
          <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ADVANCED DETAILS · methodology, target venue, references…</span>
          {advanced ? <ChevronUp className="w-4 h-4 text-kb-ink-muted" /> : <ChevronDown className="w-4 h-4 text-kb-ink-muted" />}
        </button>

        {advanced && (
          <div className="space-y-4 border border-slate-200 rounded-sm p-5 bg-slate-50/50" data-testid="idea-advanced-section">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Domain</label>
                <select className="kb-input mt-1.5" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="idea-domain-select">
                  {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Research Category (top-level)</label>
                <select className="kb-input mt-1.5" value={form.taxonomy_node_id}
                  onChange={(e) => setForm({ ...form, taxonomy_node_id: e.target.value })}
                  data-testid="idea-taxonomy-select">
                  <option value="">— Select a category (optional) —</option>
                  {topNodes.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
                <div className="kb-mono text-[10px] text-kb-ink-light mt-1">An admin reviewer may refine this to a deeper subcategory.</div>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Target Quartile</label>
                <select className="kb-input mt-1.5" value={form.target_quartile} onChange={(e) => setForm({ ...form, target_quartile: e.target.value })}>
                  {["Q1", "Q2", "Q3", "Q4", "Conference"].map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Target Journal / Conference</label>
              <input className="kb-input mt-1.5" value={form.target_venue}
                onChange={(e) => setForm({ ...form, target_venue: e.target.value })}
                placeholder="e.g. ACL Findings 2026" />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Methodology</label>
              <textarea rows={3} className="kb-input mt-1.5 resize-none" value={form.methodology}
                onChange={(e) => setForm({ ...form, methodology: e.target.value })}
                placeholder="Datasets, experiments, baselines, evaluation metrics…" />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Reference papers</label>
              <textarea rows={3} className="kb-input mt-1.5 resize-none" value={form.reference_papers}
                onChange={(e) => setForm({ ...form, reference_papers: e.target.value })}
                placeholder="One reference per line" />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Keywords (comma separated)</label>
              <input className="kb-input mt-1.5" value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="e.g. NLP, low-resource, Nepali" />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Additional notes</label>
              <textarea rows={2} className="kb-input mt-1.5 resize-none" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
        )}

        <div className="pt-2 flex items-center gap-3">
          <button type="submit" disabled={submitting} className="kb-btn-primary" data-testid="idea-submit-btn">
            {submitting ? "Submitting…" : (<>Submit for review <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <Link to="/dashboard/ideas" className="kb-btn-outline">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
